import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  closeTab as closeTabPure,
  loadTabs,
  pruneTabs,
  resolveTabTarget,
  saveTabs,
  selectTab as selectTabPure,
  setTabTitle,
  visitUrl,
  type ShellTab,
  type TabRouteRef,
  type TabsState,
} from "./tabModel";

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private windows / blocked site data throw on access.
    return null;
  }
}

export interface UseShellTabsOptions {
  routes: ReadonlyArray<TabRouteRef>;
  /** Signed-in user id; tabs are persisted per user (none when absent). */
  userId: string | null | undefined;
  /** Whether the user may open this route (app access + RBAC + admin). */
  isAllowed: (routeId: string) => boolean;
  /** Where closing the last tab lands (the Intelligence Hub, or the org's default landing). */
  homeUrl: string;
}

export interface ShellTabsApi {
  tabs: ShellTab[];
  activeKey: string | null;
  select: (key: string) => void;
  close: (key: string) => void;
}

/**
 * Header tabs wired to the router.
 *
 * - Every location change opens or focuses the matching tab and records the
 *   URL on it, so sidebar links, deep links and in-module links all land in
 *   the right tab, and returning to a tab returns to where you were.
 * - Selecting a tab navigates to that tab's last URL. Modules are not kept
 *   mounted; the route simply renders again.
 * - Tabs persist per signed-in user and are pruned whenever access changes.
 */
export function useShellTabs({ routes, userId, isAllowed, homeUrl }: UseShellTabsOptions): ShellTabsApi {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAllowedRef = useRef(isAllowed);
  isAllowedRef.current = isAllowed;

  const [state, setState] = useState<TabsState>(() =>
    loadTabs(browserStorage(), userId, (id) => isAllowedRef.current(id)),
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const currentUrlRef = useRef(currentUrl);
  currentUrlRef.current = currentUrl;

  // A different account on the same browser gets its own tabs.
  const loadedForRef = useRef(userId);

  // Arriving anywhere opens or focuses the matching tab.
  useEffect(() => {
    const reload = loadedForRef.current !== userId;
    loadedForRef.current = userId;
    const restored = reload ? loadTabs(browserStorage(), userId, (id) => isAllowedRef.current(id)) : null;
    const target = resolveTabTarget(location.pathname, routes);
    setState((prev) => {
      const base = restored ?? prev;
      if (!target || !isAllowedRef.current(target.routeId)) return base;
      return visitUrl(base, target, currentUrl);
    });
  }, [userId, routes, location.pathname, location.search, location.hash, currentUrl]);

  // Persist.
  useEffect(() => {
    saveTabs(browserStorage(), userId, state);
  }, [userId, state]);

  // Drop tabs the user can no longer open (app access loads asynchronously and
  // fails open, so this also covers tabs restored before it arrived).
  const allowedSignature = routes.map((r) => (isAllowed(r.id) ? "1" : "0")).join("");
  useEffect(() => {
    const result = pruneTabs(stateRef.current, (id) => isAllowedRef.current(id));
    if (result.state === stateRef.current) return;
    setState(result.state);
    if (result.navigateTo && result.navigateTo !== currentUrlRef.current) navigate(result.navigateTo);
  }, [allowedSignature, navigate]);

  // Document titles, read from the modules' own query caches.
  useDocTabTitles(queryClient, state.tabs, setState);

  const homeTarget = useMemo(() => {
    const path = homeUrl.split(/[?#]/)[0] || "/";
    return resolveTabTarget(path, routes) ?? { routeId: "dashboard", docId: null };
  }, [homeUrl, routes]);

  const select = useCallback(
    (key: string) => {
      const tab = stateRef.current.tabs.find((t) => t.key === key);
      if (!tab) return;
      setState((prev) => selectTabPure(prev, key));
      if (tab.url !== currentUrlRef.current) navigate(tab.url);
    },
    [navigate],
  );

  const close = useCallback(
    (key: string) => {
      const result = closeTabPure(stateRef.current, key, homeUrl, homeTarget);
      setState(result.state);
      if (result.navigateTo && result.navigateTo !== currentUrlRef.current) navigate(result.navigateTo);
    },
    [homeTarget, homeUrl, navigate],
  );

  return { tabs: state.tabs, activeKey: state.activeKey, select, close };
}

/**
 * Where each doc app keeps the open document in React Query. Read-only: the
 * shell never fetches a document itself, it only shows a title once the
 * module has loaded one, and falls back to the module name until then.
 */
const DOC_TITLE_QUERY_KEYS: Record<string, (docId: string) => readonly unknown[]> = {
  write: (id) => ["docs", "document", id],
  present: (id) => ["presentation", id],
  grid: (id) => ["grid", "spreadsheet", id],
};

function titleFromCache(client: QueryClient, tab: ShellTab): string | undefined {
  if (!tab.docId) return undefined;
  const keyOf = DOC_TITLE_QUERY_KEYS[tab.routeId];
  if (!keyOf) return undefined;
  const data = client.getQueryData<{ title?: unknown }>(keyOf(tab.docId));
  const title = data && typeof data.title === "string" ? data.title.trim() : "";
  return title || undefined;
}

function useDocTabTitles(
  client: QueryClient,
  tabs: ShellTab[],
  setState: Dispatch<SetStateAction<TabsState>>,
) {
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  useEffect(() => {
    const sync = () => {
      for (const tab of tabsRef.current) {
        const title = titleFromCache(client, tab);
        if (title && title !== tab.title) setState((prev) => setTabTitle(prev, tab.key, title));
      }
    };
    sync();
    return client.getQueryCache().subscribe((event) => {
      if (event.type === "updated" || event.type === "added") sync();
    });
  }, [client, setState]);

  // New tabs whose document is already cached (opened from a list that
  // prefetched it) pick their title up immediately.
  const signature = tabs.map((t) => `${t.key}:${t.docId ?? ""}`).join("|");
  useEffect(() => {
    for (const tab of tabsRef.current) {
      const title = titleFromCache(client, tab);
      if (title && title !== tab.title) setState((prev) => setTabTitle(prev, tab.key, title));
    }
  }, [client, signature, setState]);
}
